import getSymbolFromCurrency from 'currency-symbol-map';
import { getReccuringLabel } from 'helpers/subscriptions';
import { SubscriptionDetailsDto, SubscriptionPriceDto } from "models/subscriptions";
import React, { useEffect, useState } from 'react';
import { useCommunity } from "./CommunityContext";

type Props = {
  options: SubscriptionDetailsDto[];
  selectedSubscription: SubscriptionDetailsDto | null;
  selectedPrice: SubscriptionPriceDto | null;
  onSelectSubscription: (subscription: SubscriptionDetailsDto) => void;
  onSelectPrice: (price: SubscriptionPriceDto) => void;
  isSubscriptionLocked?: boolean;
  isPriceLocked?: boolean;
  isError: boolean;
  errorText: string;
}

const MembershipSelection: React.FC<Props> = ({ 
  options, 
  selectedSubscription, 
  selectedPrice, 
  onSelectSubscription, 
  onSelectPrice, 
  isError, 
  errorText 
}) => {
  const { texts: { membershipPage } } = useCommunity();
  const [currentRecurringIndex, setCurrentRecurringIndex] = useState(0);

  useEffect(() => {
    if (selectedSubscription && selectedPrice){
      const priceIndex = selectedSubscription?.prices.findIndex(price => price.id === selectedPrice.id);
      if (priceIndex !== -1) {
        setCurrentRecurringIndex(priceIndex);
      } else {
        setCurrentRecurringIndex(0);
      }
    }
  }, [selectedPrice])

  useEffect(() => {
    if (options.length === 1 && !selectedSubscription) {
      const subscription = options[0];
      onSelectSubscription(subscription);
      
      if (subscription.prices.length === 1 && !selectedPrice) {
        onSelectPrice(subscription.prices[0]);
      }
    }
  }, [options, selectedSubscription, selectedPrice]);

  const handleToggle = () => {
    if (!selectedSubscription) return;
    
    const currentIndex = selectedSubscription.prices.findIndex(price => price.id === selectedPrice?.id);
    const nextIndex = (currentIndex + 1) % selectedSubscription.prices.length;
    onSelectPrice(selectedSubscription.prices[nextIndex]);
    setCurrentRecurringIndex(nextIndex);
  };

  const handlePeriodChange = (price: SubscriptionPriceDto) => {
    onSelectPrice(price);
  };

  const getPeriodLabel = (price: SubscriptionPriceDto) => {
    return price.reccuring
    ? `Per ${getReccuringLabel(price.reccuring).toLowerCase()}`
    : '';
  };

  return (
    <div className="membership-wrapper">
      <div className="membership-choices">
        {options.map((option) => (
          <div
            key={option.id}
            className={selectedSubscription?.id === option.id ? 'membership-choice membership-choice-selected' : 'membership-choice'}
            onClick={() => onSelectSubscription(option)}
          >
            <div className="membership-title-container">
              <span className="membership-radio">
                <span className={`membership-radio-dot ${selectedSubscription?.id === option.id ? 'active' : ''}`} />
              </span>
              <span className="membership-choice-text">{option.name}</span>
            </div>
            <div className="membership-choice-content">
              {selectedSubscription?.id === option.id && (
                <>
                  <div
                    className="membership-description"
                    dangerouslySetInnerHTML={{__html: option.description}}
                  />
                  {option?.benefitsLink && (
                    <div className="membership-description-link">
                      <a href={option.benefitsLink} target="_blank" rel="noopener noreferrer">
                        {membershipPage?.benefitsLink || 'See details here'}
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {selectedPrice && selectedSubscription && (
        <div className="membership-price-block">
          <div className="membership-price-row">
            <div className="membership-price-col">
              <div className="membership-price">
                {`${getSymbolFromCurrency(selectedPrice.currency)} ${selectedPrice.amount / 100}`}
              </div>
              <div className="membership-per-month">{getPeriodLabel(selectedPrice).toUpperCase()}</div>
            </div>
            
            {selectedSubscription.prices.length === 2 && (
              <div className="membership-toggle-row">
                <span className="membership-toggle-label">
                  {getReccuringLabel(selectedSubscription.prices[0].reccuring)}
                </span>
                <span
                  className="membership-toggle"
                  onClick={handleToggle}
                >
                  <span
                    className="membership-toggle-circle"
                    style={{ left: currentRecurringIndex === 0 ? 2 : 17 }}
                  />
                </span>
                <span className="membership-toggle-label">
                  {getReccuringLabel(selectedSubscription.prices[1].reccuring)}
                </span>
              </div>
            )}
            
            {selectedSubscription.prices.length > 2 && (
              <div className="membership-multi-toggle-row">
                {selectedSubscription.prices.map((price, idx) => (
                  <button
                    key={`${price.id}-${idx}`}
                    className={
                      idx === currentRecurringIndex
                        ? 'membership-multi-toggle-btn active'
                        : 'membership-multi-toggle-btn'
                    }
                    onClick={() => {
                      setCurrentRecurringIndex(idx);
                      handlePeriodChange(price);
                    }}
                    type="button"
                  >
                    {getReccuringLabel(price.reccuring)}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="membership-onboarding-asterisk-container">
            {(selectedSubscription?.joiningFee && Number(selectedSubscription.joiningFee) > 0) ? (
              <div className="membership-onboarding-fee">
                {getSymbolFromCurrency(selectedPrice.currency)}{selectedSubscription.joiningFee} one-time onboarding fee
              </div>
            ) : null}
          </div>
        </div>
      )}
      
      {isError && errorText && (
        <div className="membership-date-error">{errorText}</div>
      )}
    </div>
  );
};

export default MembershipSelection; 